import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import LiveStrip from "../components/LiveStrip";
import FloatingSupportChat from "../components/FloatingSupportChat";
import HeathrowLiveStatusCompact from "../components/HeathrowLiveStatusCompact";
import CcgPremiumReviews from "../components/CcgPremiumReviews";
import PremiumContactStrip from "../components/PremiumContactStrip";
import PostcodeInput from "../components/PostcodeInput";
import api from "../utils/api";
import { trackFunnelEvent } from "../utils/funnelTracking";
import { RouteIntelligencePreview2, RoutePricePreview2 } from "../components/RoutePreview2";
import "./Home.css";

const GOOGLE_REVIEW_URL = "https://share.google/trIAb3GAvw55L1l8S";

const trustBadges = [
  {
    icon: "✓",
    eyebrow: "GOV.UK registered operator",
    title: "Licensed & insured",
    body: "PSV licence PF2071924",
  },
  {
    icon: "G",
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "View our reviews",
    href: GOOGLE_REVIEW_URL,
  },
];

const heroBenefits = [
  { icon: "£", title: "Fixed prices", body: "No hidden fees" },
  { icon: "○", title: "Free waiting", body: "60 minutes" },
  { icon: "✈", title: "Flight monitoring", body: "We track your flight" },
  { icon: "☎", title: "24/7 support", body: "Always here" },
];

const operatorCards = [
  {
    icon: "♔",
    eyebrow: "GOV.UK registered",
    title: "Licensed & insured",
    body: "Public Carrier Licence",
  },
  {
    icon: "G",
    eyebrow: "Google reviews",
    title: "4.8 ★★★★★",
    body: "View our reviews",
    href: GOOGLE_REVIEW_URL,
  },
  {
    icon: "👥",
    eyebrow: "1000s of journeys",
    title: "Completed",
    body: "Across the UK",
  },
  {
    icon: "♬",
    eyebrow: "24/7 support",
    title: "Always here",
    body: "Before, during & after",
  },
];

const reasonCards = [
  { icon: "✓", title: "Punctual & reliable", body: "98% on-time record" },
  { icon: "▣", title: "Modern coach fleet", body: "25 to 53 seaters" },
  { icon: "👤", title: "Professional drivers", body: "Fully licensed & vetted" },
  { icon: "✦", title: "Flexible & friendly", body: "We adapt to you" },
];

const intelligenceCards = [
  {
    title: "Large group timing",
    body: "Plan airport, hotel and event journeys where loading time, route timing and passenger coordination matter.",
  },
  {
    title: "Airport awareness",
    body: "Use the same live airport intelligence layer already used across the CheapCabGrab travel tools.",
  },
  {
    title: "Coach-first quote flow",
    body: "Start with coach-sized quote settings so larger group options are relevant from the first search.",
  },
  {
    title: "Booking handoff",
    body: "Move from quote to results and booking without a separate backend or new database flow.",
  },
];

const operatorTrustItems = [
  {
    title: "RHP Minibus Hire UK Ltd",
    body: "Transport support from an active UK company listed at Companies House under company number 10377899.",
  },
  {
    title: "PSV operator licence PF2071924",
    body: "Licence reference can be checked through GOV.UK's public vehicle operator licence search.",
  },
  {
    title: "Licensed carriers",
    body: "Companies House lists the business activity as licensed carriers, supporting professional group transport.",
  },
];

const tripTypes = [
  "Airport transfers for schools, teams and tour groups",
  "Corporate events, conferences and staff travel",
  "London hotel shuttles and multi-stop itineraries",
  "Weddings, attractions, stadiums and UK day trips",
];

const fleetOptions = [
  {
    title: "25-seater mini coach",
    body: "Best for medium-sized groups, corporate events, airport transfers, weddings and special occasions.",
  },
  {
    title: "33 to 53-seater coaches",
    body: "For larger groups, schools, tours, conferences and coordinated airport or venue travel.",
  },
  {
    title: "Need a minibus?",
    body: "For smaller groups, MinibusGrab handles 8, 9, 14 and 16 seater quote flows through the same platform.",
    link: "/minibus-hire",
  },
];

const localHeathrowRoutes = [
  { title: "Slough to Heathrow coach hire", href: "/slough-to-heathrow-coach" },
  { title: "Windsor to Heathrow coach hire" },
  { title: "Burnham to Heathrow coach hire" },
  { title: "Taplow to Heathrow coach hire" },
  { title: "Maidenhead to Heathrow coach hire" },
  { title: "Langley to Heathrow coach hire" },
  { title: "Marlow to Heathrow coach hire" },
  { title: "Henley to Heathrow coach hire" },
];

const driverTrustItems = [
  {
    title: "Licensed and vetted drivers",
    body: "Drivers are council licensed, DBS checked where required and assigned through approved licensed operator processes.",
  },
  {
    title: "Airport-specialist timing",
    body: "Heathrow, Gatwick, Luton and Stansted journeys are planned around flight tracking, terminal routes and group loading time.",
  },
  {
    title: "Professional conduct",
    body: "Expect clear route planning, help with luggage, clean vehicles, respectful service and fixed fares with no surge pricing.",
  },
  {
    title: "Safeguarding awareness",
    body: "Licensed group transport work includes safe conduct, disability awareness and support for vulnerable passengers where relevant.",
  },
];

const faqItems = [
  {
    question: "Can we book coach hire for airport transfers?",
    answer:
      "Yes. CheapCoachGrab is built for airport transfers, school groups, business teams, tours and larger group travel.",
  },
  {
    question: "Are coaches instant booking?",
    answer:
      "Large coach jobs usually need confirmation for vehicle availability, access, loading points and route details.",
  },
  {
    question: "Can coaches handle luggage?",
    answer:
      "Yes, but luggage requirements matter. Add passenger and luggage details so the right vehicle can be recommended.",
  },
  {
    question: "Can we plan multi-stop journeys?",
    answer:
      "Yes. Multi-stop coach journeys can be handled, but detailed timings and pickup points should be confirmed before travel.",
  },
];

const destinationGroups = [
  {
    title: "Airports",
    body: "Heathrow, Gatwick, Luton, Stansted and UK airport transfers for large groups travelling together.",
  },
  {
    title: "Events",
    body: "Corporate events, weddings, sports teams, conferences and venue transfers.",
  },
  {
    title: "Attractions",
    body: "Coach travel to Windsor Castle, Legoland, Thorpe Park, central London sights and UK day trips.",
  },
];

const pad = (value) => String(value).padStart(2, "0");

const fallbackHeathrowLive = {
  code: "LHR",
  name: "Heathrow",
  overall: { label: "Checking", reason: "Live Heathrow status is updating." },
  hero_status: {
    road: { label: "Checking", headline: "Road and motorway checks are updating." },
    rail: { label: "Checking", headline: "Rail and TfL checks are updating." },
    flights: { label: "Checking", headline: "Flight disruption checks are updating." },
    weather: { label: "Checking", headline: "Weather checks are updating." },
  },
  summary: { headline: "Live sources are checking." },
  preview: {
    terminal: "Terminals checking",
    terminal_pressure: [],
    terminal_queues: [],
    queues: ["Security and border queue checks are updating."],
    weather: "Weather checking",
  },
};

const localDatePartsInHours = (hoursAhead) => {
  const date = new Date(Date.now() + hoursAhead * 60 * 60 * 1000);
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const extractPostcode = (text = "") => {
  const upper = text.toUpperCase();
  const match = upper.match(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}\b/);
  if (match) return match[0].replace(/\s+/g, "");
  if (upper.includes("HEATHROW") || upper.includes("LHR")) return "TW6";
  return text.trim();
};

const statusToneKey = (value) => {
  const text = String(value || "").toLowerCase();
  if (text.includes("critical") || text.includes("severe") || text.includes("high") || text.includes("red") || text.includes("poor")) return "red";
  if (text.includes("moderate") || text.includes("busy") || text.includes("amber") || text.includes("watch") || text.includes("delay")) return "amber";
  if (text.includes("good") || text.includes("green") || text.includes("clear") || text.includes("normal") || text.includes("on time")) return "green";
  return "checking";
};

const cleanLiveText = (value, fallback = "Live status updating") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return fallback;
  return text.length > 92 ? `${text.slice(0, 89).trim()}...` : text;
};

const statusRank = (value) => {
  const tone = statusToneKey(value);
  if (tone === "red") return 4;
  if (tone === "amber") return 3;
  if (tone === "green") return 2;
  return 1;
};

const queueWaitRank = (wait = "") => {
  const text = String(wait || "").toLowerCase();
  const ranges = [...text.matchAll(/(\d+)\s*-\s*(\d+)/g)].map((match) => Number(match[2]));
  const singles = [...text.matchAll(/(?:^|[^\d])(\d+)(?:\s*\+|\s*min|$)/g)].map((match) => Number(match[1]));
  const lessThan = text.match(/<\s*(\d+)/);
  if (ranges.length) return Math.max(...ranges);
  if (singles.length) return Math.max(...singles);
  if (lessThan) return Number(lessThan[1]) - 1;
  return 0;
};

const queueLineText = (line = {}) => `${line.label || "Queue"}: ${line.wait || "checking"}`;

const formatQueueLines = (lines = []) => {
  const cleanLines = Array.isArray(lines) ? lines.filter(Boolean) : [];
  if (!cleanLines.length) return "Security and border snapshot";
  const sorted = cleanLines
    .slice()
    .sort((a, b) => queueWaitRank(b.wait) - queueWaitRank(a.wait));
  const worst = sorted[0];
  const rest = cleanLines.filter((line) => line !== worst);
  return [worst, ...rest].map(queueLineText).join(" • ");
};

const deriveTerminalLiveStatus = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure : [];
  const strongest = structured
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: `${strongest.terminal || "Terminal"} pressure`,
    };
  }
  const text = String(preview.terminal || "");
  if (/severe|red|poor|high/i.test(text)) return { label: "High", detail: cleanLiveText(text, "Terminal pressure elevated") };
  if (/busy|moderate|amber|pressure/i.test(text)) return { label: "Moderate", detail: cleanLiveText(text, "Terminal pressure moderate") };
  if (/good|green|normal/i.test(text)) return { label: "Good", detail: cleanLiveText(text, "Terminal flow normal") };
  return { label: "Checking", detail: "Terminal pressure updating" };
};

const deriveQueueLiveStatus = (preview = {}) => {
  const queues = Array.isArray(preview.terminal_queues) ? preview.terminal_queues : [];
  const strongest = queues
    .slice()
    .sort((a, b) => statusRank(b?.label || b?.level) - statusRank(a?.label || a?.level))[0];
  if (strongest) {
    const wait = formatQueueLines(strongest.lines || []);
    return {
      label: strongest.label || strongest.level || "Checking",
      detail: cleanLiveText(`${strongest.terminal || "Queue"} • ${wait}`, "Queue snapshot updating"),
    };
  }
  const firstQueue = Array.isArray(preview.queues) ? preview.queues.find(Boolean) : "";
  if (/severe|red|45|60|busy/i.test(firstQueue)) return { label: "Busy", detail: cleanLiveText(firstQueue, "Queues busy") };
  if (/30|moderate|amber/i.test(firstQueue)) return { label: "Moderate", detail: cleanLiveText(firstQueue, "Queues moderate") };
  if (/good|green|0-5|5-10/i.test(firstQueue)) return { label: "Good", detail: cleanLiveText(firstQueue, "Queues look good") };
  return { label: "Checking", detail: cleanLiveText(firstQueue, "Security and border queues updating") };
};

const formatLiveAge = (value) => {
  if (!value) return "Auto-updated every 2 min";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Auto-updated every 2 min";
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 90) return "Just updated";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min old`;
  return `${Math.round(seconds / 3600)} hr old`;
};

const buildHeathrowLiveCards = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  const terminals = deriveTerminalLiveStatus(preview);
  const queues = deriveQueueLiveStatus(preview);
  const weatherDetail = hero.weather?.headline || hero.weather?.reason || preview.weather;

  return [
    { key: "road", icon: "▰", label: "Roads", value: hero.road?.label || "Checking", detail: hero.road?.headline || hero.road?.reason || "Road checks updating" },
    { key: "rail", icon: "▤", label: "Rail", value: hero.rail?.label || "Checking", detail: hero.rail?.headline || hero.rail?.reason || "Rail checks updating" },
    { key: "flights", icon: "✈", label: "Flights", value: hero.flights?.label || "Checking", detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating" },
    { key: "terminals", icon: "▦", label: "Terminals", value: terminals.label, detail: terminals.detail },
    { key: "queues", icon: "◫", label: "Queues", value: queues.label, detail: queues.detail },
    { key: "weather", icon: "☁", label: "Weather", value: hero.weather?.label || "Checking", detail: weatherDetail || "Weather checks updating" },
  ];
};

const asLiveList = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return fallback.filter(Boolean);
};

const liveTextItem = (value, fallbackStatus = "Checking") => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const parts = text.split(" • ").map((part) => part.trim()).filter(Boolean);
  const status = parts.length > 1 ? parts[parts.length - 1] : fallbackStatus;
  return {
    title: parts[0] || text || "Status checking",
    detail: parts.length > 2 ? parts.slice(1, -1).join(" • ") : "",
    status: /good|clear|on time|moderate|busy|watch|severe|high|delay|checking/i.test(status) ? status : fallbackStatus,
  };
};

const terminalPressureItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_pressure) ? preview.terminal_pressure.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Terminal",
      detail: `${Number(item.flights || 0)} flights • ${Number(item.delayed_30 || 0)} delayed 30m+`,
      status: item.label || item.level || "Checking",
    }));
  }
  return String(preview.terminal || "Terminals checking")
    .split("•")
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const terminalQueueItems = (preview = {}) => {
  const structured = Array.isArray(preview.terminal_queues) ? preview.terminal_queues.filter(Boolean) : [];
  if (structured.length) {
    return structured.slice(0, 4).map((item) => ({
      title: item.terminal || "Queue",
      detail: formatQueueLines(item.lines || []),
      status: item.label || item.level || "Checking",
    }));
  }
  return asLiveList(preview.queues, ["Security and border queue checks are updating."])
    .map((item) => liveTextItem(item, "Checking"))
    .slice(0, 4);
};

const detailedHeathrowGroups = (airport = fallbackHeathrowLive) => {
  const hero = airport.hero_status || {};
  const preview = airport.preview || {};
  return [
    {
      key: "road",
      title: "Road corridors",
      subtitle: "Major routes and visible traffic pressure",
      icon: "▰",
      status: hero.road?.label || "Checking",
      items: asLiveList(preview.road, [hero.road?.headline || hero.road?.reason || "Road checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.road?.label || "Checking")),
    },
    {
      key: "flights",
      title: "Flights",
      subtitle: "Disruption, arrivals and departures",
      icon: "✈",
      status: hero.flights?.label || "Checking",
      items: [
        {
          title: "Current flight pressure",
          detail: hero.flights?.headline || hero.flights?.reason || "Flight checks updating",
          status: hero.flights?.label || "Checking",
        },
        {
          title: "Airport flow",
          detail: `${preview.arrivals || "0"} arrivals • ${preview.departures || "0"} departures`,
          status: hero.flights?.label || "Checking",
        },
        ...asLiveList(preview.flights).slice(0, 1).map((item) => liveTextItem(item, hero.flights?.label || "Checking")),
      ],
    },
    {
      key: "rail",
      title: "Rail and Underground",
      subtitle: "Airport stations, lines and route status",
      icon: "▤",
      status: hero.rail?.label || "Checking",
      items: asLiveList(preview.rail, [hero.rail?.headline || hero.rail?.reason || "Rail checks updating"]).slice(0, 4).map((item) => liveTextItem(item, hero.rail?.label || "Checking")),
    },
    {
      key: "terminals",
      title: "Terminal pressure",
      subtitle: "Flight delay and cancellation status by terminal",
      icon: "▦",
      status: deriveTerminalLiveStatus(preview).label,
      items: terminalPressureItems(preview),
    },
    {
      key: "queues",
      title: "Queue snapshot",
      subtitle: "Security and border waits by terminal",
      icon: "◫",
      status: deriveQueueLiveStatus(preview).label,
      items: terminalQueueItems(preview),
    },
    {
      key: "weather",
      title: "Weather at LHR",
      subtitle: "Weather included in leave-time checks",
      icon: "☁",
      status: hero.weather?.label || "Checking",
      items: [
        {
          title: preview.weather || "Weather checking",
          detail: hero.weather?.headline || hero.weather?.reason || "Live weather checks updating",
          status: hero.weather?.label || "Checking",
        },
      ],
    },
  ];
};

function normalizeHeathrowPlace(value = "") {
  const upper = value.toUpperCase();
  if (upper.includes("HEATHROW TERMINAL 5")) return "Heathrow Terminal 5";
  if (upper.includes("HEATHROW TERMINAL 4")) return "Heathrow Terminal 4";
  if (upper.includes("HEATHROW TERMINAL 3")) return "Heathrow Terminal 3";
  if (upper.includes("HEATHROW TERMINAL 2")) return "Heathrow Terminal 2";
  if (upper.includes("HEATHROW")) return value || "Heathrow Airport";
  return value;
}

function buildQuoteLocationString(text) {
  const cleanedText = String(text || "").trim();
  if (!cleanedText) return "";
  return extractPostcode(cleanedText);
}

function openSupportChat() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("ccg:open-support-chat", { detail: { flow: "enquiry" } }));
}

const PremiumLocationField = ({ ariaLabel, value, onChange, onPlaceSelected, placeholder, autoComplete }) => {
  return (
    <label className="minibus-v2-field minibus-v2-field-wide minibus-v2-location-field">
      <PostcodeInput
        value={value}
        onChange={onChange}
        onPlaceSelected={onPlaceSelected}
        placeholder={placeholder}
        restrictions={{ country: ["gb"] }}
        className="minibus-v2-location-input"
        style={{
          marginBottom: 0,
          height: "auto",
          minHeight: 0,
          padding: 0,
          border: 0,
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
        }}
        inputProps={{
          "aria-label": ariaLabel,
          autoComplete,
        }}
      />
    </label>
  );
};

const PremiumCoachQuoteForm = () => {
  const navigate = useNavigate();
  const initialDateTime = useMemo(() => localDatePartsInHours(25), []);
  const minDateTime = useMemo(() => localDatePartsInHours(4), []);
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [date, setDate] = useState(initialDateTime.date);
  const [time, setTime] = useState(initialDateTime.time);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pickupTime = `${date}T${time}`;
  const minPickupTime = `${minDateTime.date}T${minDateTime.time}`;

  const enforceMinimumPickupTime = (nextDate, nextTime) => {
    const nextPickupTime = `${nextDate}T${nextTime}`;
    if (nextPickupTime < minPickupTime) {
      setDate(minDateTime.date);
      setTime(minDateTime.time);
      return;
    }
    setDate(nextDate);
    setTime(nextTime);
  };

  const getQuote = async () => {
    if (!pickup.trim() || !dropoff.trim()) {
      setError("Please enter both pickup and drop-off details.");
      return;
    }
    if (!date || !time) {
      setError("Please choose both pickup date and time.");
      return;
    }
    if (pickupTime < minPickupTime) {
      setError("Please choose a pickup time at least 4 hours from now.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup: buildQuoteLocationString(pickup),
        dropoff: buildQuoteLocationString(dropoff),
        pickup_time: pickupTime,
        passengers: 25,
        bags: 0,
      });

      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        navigate(`/coach-results/${data.quote_id}/${data.access_token}?vehicle=twentyfive`);
        return;
      }
      if (data.comingSoon) {
        setError("Sorry, we are not yet operating full coverage for this route. Please try another nearby pickup area.");
        return;
      }
      setError(data.error || "We could not calculate a coach quote for this journey. Please check the details and try again.");
    } catch (err) {
      setError(err.response?.data?.error || "Our quote system is temporarily busy. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="minibus-v2-quote-card" id="cheapcoachgrab-quote-form">
      <div className="minibus-v2-quote-head">
        <span>Get instant quotes</span>
        <small><i /> Free • Fast • No obligation</small>
      </div>

      <div className="minibus-v2-quote-grid">
        <PremiumLocationField
          ariaLabel="Pickup postcode or address"
          value={pickup}
          onChange={setPickup}
          onPlaceSelected={(place) => {
            const nextValue = place?.formatted_address || place?.name || "";
            setPickup(nextValue);
          }}
          placeholder="Enter pickup postcode or address"
          autoComplete="street-address"
        />

        <PremiumLocationField
          ariaLabel="Drop-off postcode or address"
          value={dropoff}
          onChange={setDropoff}
          onPlaceSelected={(place) => {
            const nextValue = normalizeHeathrowPlace(place?.formatted_address || place?.name || "");
            setDropoff(nextValue);
          }}
          placeholder="Enter dropoff postcode or address"
          autoComplete="street-address"
        />

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup date</span>
          <input
            aria-label="Pickup date"
            type="date"
            value={date}
            min={minDateTime.date}
            onChange={(event) => enforceMinimumPickupTime(event.target.value, time)}
          />
        </label>

        <label className="minibus-v2-field">
          <span className="minibus-v2-sr-only">Pickup time</span>
          <input
            aria-label="Pickup time"
            type="time"
            value={time}
            onChange={(event) => enforceMinimumPickupTime(date, event.target.value)}
          />
        </label>

        <div className="minibus-v2-quote-actions">
          <button type="button" className="minibus-v2-quote-btn" onClick={getQuote} disabled={loading}>
            {loading ? "Checking..." : "Get quotes now"} <span>→</span>
          </button>
          <button type="button" className="minibus-v2-clear-btn" onClick={openSupportChat}>
            Need help?
          </button>
          <small>Takes 20 seconds</small>
        </div>
      </div>

      {error ? <div className="minibus-v2-error">{error}</div> : null}

      <div className="minibus-v2-quote-promises">
        <span>Best price guarantee</span>
        <span>No hidden charges</span>
        <span>Free 60-min waiting</span>
        <span>Secure online booking</span>
        <span>Instant email confirmation</span>
      </div>

      <PremiumContactStrip title="Need help with your coach quote?" />
    </div>
  );
};

const PremiumReviewsBar = () => (
  <section className="minibus-v2-reviews minibus-v2-premium-reviews" aria-label="Customer reviews and trust signals">
    <CcgPremiumReviews pageKey="/coach-hire" />
  </section>
);

const PremiumHeathrowStatus = () => (
  <HeathrowLiveStatusCompact
    title="Heathrow (LHR)"
    kicker="Live airport intelligence"
    className="minibus-v2-status"
    footnote="Roads, flights, terminals, queues, rail and weather are checked before coach journeys."
  />
);

const PremiumHeathrowDetailStatus = () => {
  const [airport, setAirport] = useState(fallbackHeathrowLive);

  useEffect(() => {
    let cancelled = false;

    const loadHeathrowStatus = async () => {
      try {
        const res = await api.get("/api/open/airport-intelligence-summary?airports=LHR");
        if (cancelled) return;
        const liveHeathrow = (res?.data?.airports || []).find((item) => String(item?.code || "").toUpperCase() === "LHR");
        setAirport(liveHeathrow || fallbackHeathrowLive);
      } catch (err) {
        if (!cancelled) setAirport(fallbackHeathrowLive);
      }
    };

    loadHeathrowStatus();
    const timer = setInterval(loadHeathrowStatus, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const detailedGroups = detailedHeathrowGroups(airport);

  return (
      <section className="minibus-v2-lhr-detail" aria-label="Detailed Heathrow status preview">
        <div className="minibus-v2-content-header minibus-v2-lhr-detail-head">
          <span className="minibus-v2-live-kicker"><i /> Detailed LHR status</span>
          <h2>Heathrow journey checks before you book</h2>
          <p>Roads, rail, flights, terminals, queues and weather are grouped into a travel-ready preview for coach journeys.</p>
        </div>
        <div className="minibus-v2-lhr-detail-grid">
          {detailedGroups.map((group) => {
            const tone = statusToneKey(group.status);
            return (
              <article className={`minibus-v2-lhr-detail-card is-${tone}`} key={group.key}>
                <div className="minibus-v2-lhr-detail-card-head">
                  <b>{group.icon}</b>
                  <span>
                    <strong>{group.title}</strong>
                    <small>{group.subtitle}</small>
                  </span>
                  <em>{group.status || "Checking"}</em>
                </div>
                <div className="minibus-v2-lhr-detail-list">
                  {group.items.map((item, index) => (
                    <div key={`${group.key}-${item.title}-${index}`}>
                      <span>
                        <strong>{cleanLiveText(item.title, "Status checking")}</strong>
                        <small>{cleanLiveText(item.detail, "Live check updating")}</small>
                      </span>
                      <em className={`is-${statusToneKey(item.status)}`}>{item.status || group.status || "Checking"}</em>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>
  );
};

const HomeCoachGrab = () => {
  useEffect(() => {
    trackFunnelEvent("landing_view", {
      brand: "cheapcoachgrab",
      source: "brand_home",
    });
  }, []);

  useEffect(() => {
    const title = "Heathrow Coach Hire | 25 to 53 Seater Airport Transfers | CheapCoachGrab";
    const description =
      "Book Heathrow coach hire for 25 seater, 33 seater and 49 to 53 seater group transfers from Slough, Windsor, Burnham, Taplow, Maidenhead, Langley, Marlow and Henley. Licensed PSV operator support, fixed quotes and live LHR road, rail, flight, terminal and queue status.";
    const keywords =
      "Heathrow coach hire, Heathrow 25 seater coach, 33 seater coach Heathrow, 49 seater coach Heathrow, 53 seater coach Heathrow, airport coach hire, London coach hire, Slough to Heathrow coach hire, Windsor to Heathrow coach hire, Burnham to Heathrow coach hire, Taplow to Heathrow coach hire, Maidenhead to Heathrow coach hire, Langley to Heathrow coach hire, Marlow to Heathrow coach hire, Henley to Heathrow coach hire, school coach hire, corporate coach hire, event coach hire, licensed coach drivers, PSV operator licence PF2071924";
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([key, value]) => {
        el[key] = value;
        if (typeof el.setAttribute === "function") el.setAttribute(key, value);
      });
    };

    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', {
      property: "og:description",
      content:
        "Premium Heathrow coach quotes for 25 to 53 seater group transfers, backed by licensed PSV operator trust signals and live LHR road, rail, flight, terminal and queue intelligence.",
    });
    setMeta('meta[property="og:url"]', { property: "og:url", content: "https://www.cheapcoachgrab.com/" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: "https://www.cheapcoachgrab.com/coach-hero.png" });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: "Heathrow Coach Hire | CheapCoachGrab" });
    setMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: "Fixed Heathrow coach quotes with live LHR status, licensed PSV operator trust and 25 to 53 seater group travel options.",
    });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: "https://www.cheapcoachgrab.com/coach-hero.png" });

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.cheapcoachgrab.com/");

    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": "https://www.cheapcoachgrab.com/#website",
          name: "CheapCoachGrab",
          url: "https://www.cheapcoachgrab.com/",
          description: "Heathrow coach hire quotes for 25 to 53 seater airport transfers, London trips and UK group travel.",
          inLanguage: "en-GB",
        },
        {
          "@type": "Organization",
          "@id": "https://www.cheapcoachgrab.com/#organization",
          name: "CheapCoachGrab",
          url: "https://www.cheapcoachgrab.com/",
          logo: "https://www.cheapcoachgrab.com/logo.png",
          sameAs: [
            "https://find-and-update.company-information.service.gov.uk/company/10377899",
            "https://www.gov.uk/find-vehicle-operators",
          ],
        },
        {
          "@type": "Organization",
          "@id": "https://www.cheapcoachgrab.com/#licensed-operator",
          name: "RHP Minibus Hire UK Ltd",
          legalName: "RHP MINIBUS HIRE UK LIMITED",
          identifier: [
            { "@type": "PropertyValue", propertyID: "Companies House number", value: "10377899" },
            { "@type": "PropertyValue", propertyID: "PSV operator licence", value: "PF2071924" },
          ],
        },
        {
          "@type": "Service",
          "@id": "https://www.cheapcoachgrab.com/#coach-hire",
          name: "Heathrow coach hire",
          alternateName: ["Heathrow 25 seater coach", "Heathrow 33 seater coach", "Heathrow 53 seater coach", "Heathrow group airport transfer"],
          serviceType: "Licensed coach hire and group airport transfers with live Heathrow airport intelligence",
          provider: { "@id": "https://www.cheapcoachgrab.com/#organization" },
          areaServed: [
            "Heathrow Airport",
            "Slough",
            "Windsor",
            "Burnham",
            "Taplow",
            "Maidenhead",
            "Langley",
            "Marlow",
            "Henley-on-Thames",
            "London",
            "Gatwick Airport",
            "Luton Airport",
            "Stansted Airport",
            "United Kingdom",
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Heathrow coach vehicle options",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "25 seater Heathrow coach hire" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "33 seater Heathrow coach hire" } },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "49 to 53 seater Heathrow coach hire" } },
            ],
          },
        },
      ],
    };
    let jsonLd = document.getElementById("cheapcoachgrab-jsonld");
    if (!jsonLd) {
      jsonLd = document.createElement("script");
      jsonLd.id = "cheapcoachgrab-jsonld";
      jsonLd.type = "application/ld+json";
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify(structuredData);
  }, []);

  const scrollToForm = () => {
    const el = document.getElementById("cheapcoachgrab-quote-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="home-page coachgrab-home coachgrab-v2-home minibusgrab-v2-home">
      <Helmet>
        <title>Heathrow Coach Hire | 25 to 53 Seater Airport Transfers | CheapCoachGrab</title>
        <meta
          name="description"
          content="Book Heathrow coach hire for 25 seater, 33 seater and 49 to 53 seater group transfers from Slough, Windsor, Burnham, Taplow, Maidenhead, Langley, Marlow and Henley. Licensed PSV operator support, fixed quotes and live LHR road, rail, flight, terminal and queue status."
        />
        <meta
          name="keywords"
          content="Heathrow coach hire, Heathrow 25 seater coach, 33 seater coach Heathrow, 49 seater coach Heathrow, 53 seater coach Heathrow, airport coach hire, London coach hire, Slough to Heathrow coach hire, Windsor to Heathrow coach hire, Burnham to Heathrow coach hire, Taplow to Heathrow coach hire, Maidenhead to Heathrow coach hire, Langley to Heathrow coach hire, Marlow to Heathrow coach hire, Henley to Heathrow coach hire, school coach hire, corporate coach hire, event coach hire, licensed coach drivers, PSV operator licence PF2071924"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.cheapcoachgrab.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heathrow Coach Hire | 25 to 53 Seater Transfers | CheapCoachGrab" />
        <meta
          property="og:description"
          content="Premium Heathrow coach quotes for 25 to 53 seater group transfers, backed by licensed PSV operator trust signals and live LHR road, rail, flight, terminal and queue intelligence."
        />
        <meta property="og:url" content="https://www.cheapcoachgrab.com/" />
        <meta property="og:image" content="https://www.cheapcoachgrab.com/coach-hero.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Heathrow Coach Hire | CheapCoachGrab" />
        <meta
          name="twitter:description"
          content="Fixed Heathrow coach quotes with live LHR status, licensed PSV operator trust and 25 to 53 seater group travel options."
        />
        <meta name="twitter:image" content="https://www.cheapcoachgrab.com/coach-hero.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://www.cheapcoachgrab.com/#website",
                name: "CheapCoachGrab",
                url: "https://www.cheapcoachgrab.com/",
                description: "Heathrow coach hire quotes for 25 to 53 seater airport transfers, London trips and UK group travel.",
                inLanguage: "en-GB",
              },
              {
                "@type": "Organization",
                "@id": "https://www.cheapcoachgrab.com/#organization",
                name: "CheapCoachGrab",
                url: "https://www.cheapcoachgrab.com/",
                logo: "https://www.cheapcoachgrab.com/logo.png",
                sameAs: [
                  "https://find-and-update.company-information.service.gov.uk/company/10377899",
                  "https://www.gov.uk/find-vehicle-operators",
                ],
              },
              {
                "@type": "Organization",
                "@id": "https://www.cheapcoachgrab.com/#licensed-operator",
                name: "RHP Minibus Hire UK Ltd",
                legalName: "RHP MINIBUS HIRE UK LIMITED",
                identifier: [
                  {
                    "@type": "PropertyValue",
                    propertyID: "Companies House number",
                    value: "10377899",
                  },
                  {
                    "@type": "PropertyValue",
                    propertyID: "PSV operator licence",
                    value: "PF2071924",
                  },
                ],
              },
              {
                "@type": "Service",
                "@id": "https://www.cheapcoachgrab.com/#coach-hire",
                name: "Heathrow coach hire",
                alternateName: ["Heathrow 25 seater coach", "Heathrow 33 seater coach", "Heathrow 53 seater coach", "Heathrow group airport transfer"],
                serviceType: "Licensed coach hire and group airport transfers with live Heathrow airport intelligence",
                provider: { "@id": "https://www.cheapcoachgrab.com/#organization" },
                areaServed: [
                  "Heathrow Airport",
                  "Slough",
                  "Windsor",
                  "Burnham",
                  "Taplow",
                  "Maidenhead",
                  "Langley",
                  "Marlow",
                  "Henley-on-Thames",
                  "London",
                  "Gatwick Airport",
                  "Luton Airport",
                  "Stansted Airport",
                  "United Kingdom",
                ],
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: "Heathrow coach vehicle options",
                  itemListElement: [
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "25 seater Heathrow coach hire" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "33 seater Heathrow coach hire" } },
                    { "@type": "Offer", itemOffered: { "@type": "Service", name: "49 to 53 seater Heathrow coach hire" } },
                  ],
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <section className="minibus-v2-hero">
        <div className="minibus-v2-hero-bg" aria-hidden="true" />
        <div className="minibus-v2-hero-inner">
          <div className="minibus-v2-hero-copy">
            <h1>
              Heathrow coach hire powered by <span>live intelligence</span>
            </h1>
            <p>
              Real-time airport, traffic and rail data helps plan smarter coach timings for schools, teams,
              events and larger group journeys.
            </p>

            <div className="minibus-v2-benefits">
              {heroBenefits.map((item) => (
                <div className="minibus-v2-benefit" key={item.title}>
                  <b>{item.icon}</b>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.body}</small>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="minibus-v2-trust-badges" aria-label="Trust badges">
            {trustBadges.map((badge) => (
              <article className="minibus-v2-trust-badge" key={badge.eyebrow}>
                <b>{badge.icon}</b>
                <span>
                  <small>{badge.eyebrow}</small>
                  <strong>{badge.title}</strong>
                  <em>
                    {badge.href ? (
                      <a href={badge.href} target="_blank" rel="noopener noreferrer">{badge.body}</a>
                    ) : badge.body}
                  </em>
                </span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="minibus-v2-quote-wrap">
        <PremiumCoachQuoteForm />
      </div>

      <div className="minibus-v2-mobile-benefits" aria-label="Booking benefits">
        {heroBenefits.map((item) => (
          <div className="minibus-v2-benefit" key={item.title}>
            <b>{item.icon}</b>
            <span>
              <strong>{item.title}</strong>
              <small>{item.body}</small>
            </span>
          </div>
        ))}
      </div>

      <PremiumReviewsBar />

      <PremiumHeathrowStatus />

      <section className="minibus-v2-proof">
        <div className="minibus-v2-proof-intro">
          <h2>Your journey, <span>our responsibility</span></h2>
          <p>We’re a UK-registered operator with thousands of happy customers. Your group is in safe hands.</p>
        </div>
        {operatorCards.map((card) => (
          <article className="minibus-v2-proof-card" key={card.eyebrow}>
            <b>{card.icon}</b>
            <span>
              <small>{card.eyebrow}</small>
              <strong>{card.title}</strong>
              <em>
                {card.href ? (
                  <a href={card.href} target="_blank" rel="noopener noreferrer">{card.body}</a>
                ) : card.body}
              </em>
            </span>
          </article>
        ))}
      </section>

      <section className="minibus-v2-reasons">
        <div className="minibus-v2-reasons-title">
          <b>✦</b>
          <h2>Why groups choose us</h2>
        </div>
        {reasonCards.map((item) => (
          <article key={item.title}>
            <b>{item.icon}</b>
            <span>
              <strong>{item.title}</strong>
              <small>{item.body}</small>
            </span>
          </article>
        ))}
      </section>

      <PremiumHeathrowDetailStatus />

      <RoutePricePreview2 routeKey="default" vehicle="coach" limit={4} title="Popular Heathrow coach previews" />
      <RouteIntelligencePreview2 routeKey="default" compact />

      <section className="minibus-v2-content minibus-v2-editorial">
        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Built for group journeys</h2>
            <p>
              CheapCoachGrab is a dedicated front door for coach travel: simple quote entry, larger group vehicle options
              and the same booking path after quote results.
            </p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {intelligenceCards.map((card) => (
              <article key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Licensed operator trust</h2>
            <p>
              Company details, licence references and operator checks are surfaced early so group transport feels clear before booking.
            </p>
          </div>
          <div className="minibus-v2-info-grid">
            {operatorTrustItems.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Popular Heathrow coach routes</h2>
            <p>Use one quote flow for local Heathrow transfers, London travel and wider UK group movement.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {localHeathrowRoutes.map((route) => (
              <article className={route.href ? "minibus-v2-route-card is-linked" : "minibus-v2-route-card"} key={route.title}>
                <h3>
                  {route.href ? <Link to={route.href}>{route.title}</Link> : route.title}
                </h3>
                <p>Enter pickup, Heathrow terminal or airport drop-off and travel time to check coach-sized options for your group.</p>
                {route.href ? <Link className="minibus-v2-inline-link" to={route.href}>View route page</Link> : null}
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Airport, hotel and event coach trips</h2>
            <p>Group transfers for Heathrow, Gatwick, Luton, Stansted, London hotels, conferences, schools and multi-stop journeys.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {tripTypes.map((trip) => (
              <article key={trip}>
                <h3>{trip}</h3>
                <p>Enter pickup, drop-off and travel time to check the right coach options for your group.</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Vehicle options</h2>
            <p>Start with coach-sized quote settings, then confirm the right vehicle for group size, access and luggage.</p>
          </div>
          <div className="minibus-v2-info-grid">
            {fleetOptions.map((option) => (
              <article key={option.title}>
                <h3>{option.title}</h3>
                <p>{option.body}</p>
                {option.link ? <Link className="minibus-v2-inline-link" to={option.link}>View minibus hire</Link> : null}
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Licensed drivers and professional standards</h2>
            <p>Group airport journeys are handled through licensed operators and professional driver standards for safe, punctual travel.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {driverTrustItems.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-platform-card">
          <div>
            <h2>Shared platform, separate brand</h2>
            <p>
              The backend, quote storage, booking flow and database stay unchanged. The CheapCoachGrab domain gives larger groups a coach-first homepage,
              brand framing and quote entry.
            </p>
          </div>
          <button type="button" className="minibus-v2-quote-btn" onClick={scrollToForm}>
            Start a coach quote <span>→</span>
          </button>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Coach hire FAQs</h2>
            <p>Answers to common group-travel questions before you request a quote.</p>
          </div>
          <div className="minibus-v2-info-grid minibus-v2-info-grid-4">
            {faqItems.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-editorial-section">
          <div className="minibus-v2-content-header">
            <h2>Airports, hotels and attractions</h2>
            <p>Use CheapCoachGrab for practical group journeys where timing, luggage, access and vehicle size matter.</p>
          </div>
          <div className="minibus-v2-info-grid">
            {destinationGroups.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>

        <div className="minibus-v2-support-card">
          <div>
            <h2>Need help planning group transport?</h2>
            <p>
              For complex routes, multi-stop journeys, school groups, larger luggage loads or coach-size groups,
              send the details and the team can advise on the right vehicle.
            </p>
          </div>
          <div className="minibus-v2-support-actions">
            <Link to="/contact">Contact group travel support</Link>
            <button type="button" onClick={scrollToForm}>Start with an online quote</button>
          </div>
        </div>
      </section>

      <button className="home-floating-cta minibus-v2-floating-cta" onClick={scrollToForm}>
        Get Coach Quote
      </button>

      <FloatingSupportChat
        brand="cheapcoachgrab"
        defaultFlow="enquiry"
        position="right"
        showAfterMs={10000}
        nudgeText="Need coach help?"
      />
    </div>
  );
};

export default HomeCoachGrab;
